import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconeAbrir, IconeBuscar, IconeConfirmado, IconeFechado } from './icones';

export interface OpcaoDoSeletor<T extends string = string> {
  id: T;
  rotulo: string;
  descricao?: string;
  /** Âmbar + confirmação antes de disparar. */
  perigoso?: boolean;
  desabilitada?: boolean;
  /** Vira o `title` de quem está desabilitada: motivo escrito, não mistério. */
  motivo?: string;
  /** Segunda dimensão: o esforço dentro do modelo. Vazio = escolha direta. */
  filhos?: { id: string; rotulo: string }[];
  /**
   * Marca visual da opção — o estado da máquina, por exemplo.
   *
   * Decorativa: é ícone, não texto. Antes esse estado viajava como `●`/`○`
   * dentro do rótulo, o que punha um caractere no meio do nome da máquina e
   * fazia o leitor de tela anunciar "desktop-birunce círculo preto".
   */
  icone?: ReactNode;
}

/** Pílula de barra (o padrão) ou campo de formulário, da largura do pai. */
export type VarianteDoSeletor = 'pilula' | 'campo';

/**
 * O campo de busca no alto do menu, para lista grande demais para rolar — o
 * catálogo do provedor de modelos tem centenas deles (F17, D12).
 *
 * Quem chama guarda o texto e entrega as opções JÁ filtradas: a busca pode ser
 * no servidor (`?busca=`), e o seletor não tem como saber disso. Por isso ele
 * não filtra nada sozinho — filtrar aqui por cima de uma lista que o servidor
 * já filtrou esconderia resultado que casou por outro campo.
 */
export interface BuscaDoSeletor {
  valor: string;
  aoMudar: (texto: string) => void;
  /** O que aparece com o campo vazio. */
  dica?: string;
  /** A resposta ainda não voltou: a lista vazia diz "buscando", não "nada". */
  carregando?: boolean;
}

/**
 * O seletor de escolha do painel — o único que existe aqui.
 *
 * No molde do MenuDeExibicao (popover absoluto, sem dependência nova), mas
 * controlado: fecha no clique-fora, na tecla Esc e na escolha, abre para cima
 * quando vive na barra do compositor. Opção perigosa (ou com `confirmacao`)
 * não dispara no clique — pede um "confirmar" inline no próprio popover, que é
 * o desenho combinado para Fable e para os modos que dispensam aprovação.
 *
 * **A política de "formulário continua no `<select>` nativo" acabou.** Ela
 * partia da ideia de que o nativo é sempre mais acessível; o que o uso mostrou
 * é outra coisa. No Windows o `<select>` aberto é uma lista branca desenhada
 * pelo sistema no meio de uma tela escura, com a fonte do sistema e a
 * espessura do sistema; no celular vira uma roda que ocupa metade do aparelho.
 * Nos dois casos a tela parece de outro programa por um instante. A conta de
 * acessibilidade se paga aqui dentro: `role="listbox"`/`option` com
 * `aria-selected`, setas/Home/End/Enter/Esc, foco visível e foco devolvido ao
 * gatilho ao fechar.
 *
 * A variante `campo` é o que um formulário precisa: largura do pai, altura de
 * campo e rótulo de fora amarrado por `aria-labelledby`. Estender foi mais
 * barato do que ter dois seletores para manter em sincronia.
 */
export function Seletor<T extends string = string>({
  rotulo,
  titulo,
  valor,
  opcoes,
  aoEscolher,
  confirmacao,
  ocupado = false,
  acima = false,
  bloqueado,
  variante = 'pilula',
  idDoRotulo,
  icone,
  busca,
}: {
  /** O que aparece fechado (ex.: "Opus 5 · high"). */
  rotulo: string;
  titulo: string;
  valor: T | null;
  opcoes: OpcaoDoSeletor<T>[];
  /**
   * `confirmado` é `true` só quando a pessoa apertou **Confirmar** na pergunta
   * inline — e é por ele que quem chama prova à API que houve confirmação.
   * Antes o painel mandava `confirmacaoFable: true` fixo em toda troca, e a
   * trava da API passava a valer só o que a tela valesse.
   */
  aoEscolher: (id: T, filho?: string, confirmado?: boolean) => void;
  /** Texto de confirmação para uma opção; `null` dispara direto. */
  confirmacao?: (opcao: OpcaoDoSeletor<T>) => string | null;
  ocupado?: boolean;
  acima?: boolean;
  /**
   * O motivo pelo qual esta escolha não existe aqui. Presente, a pílula fica
   * desabilitada e mostra o motivo — em vez de sumir, que é o que fazia a
   * sessão observada parecer capada sem dizer de quê.
   */
  bloqueado?: string;
  variante?: VarianteDoSeletor;
  /**
   * O `id` do rótulo de fora. Com ele o nome acessível vira "Papel · operador"
   * — para quê e o quê —, que é o que o `<label>` de um `<select>` dava de
   * graça e um botão sozinho não dá.
   */
  idDoRotulo?: string;
  /** Marca visual do valor escolhido, ao lado do rótulo fechado. */
  icone?: ReactNode;
  /** Um campo de busca no alto do menu. Ver `BuscaDoSeletor`. */
  busca?: BuscaDoSeletor;
}): React.JSX.Element {
  const [aberto, setAberto] = useState(false);
  const [expandida, setExpandida] = useState<T | null>(null);
  const [pendente, setPendente] = useState<{ id: T; filho?: string; texto: string } | null>(null);
  const raiz = useRef<HTMLDivElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  /** Onde o menu foi parar na janela. Nulo enquanto ele nao esta aberto. */
  const [caixa, setCaixa] = useState<{
    esquerda: number;
    largura: number;
    topo: number;
    deBaixo: number;
    alturaMax: number;
  } | null>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const botaoConfirmar = useRef<HTMLButtonElement>(null);
  const campoDeBusca = useRef<HTMLInputElement>(null);
  /** Os botões de opção na ordem da tela: é por eles que as setas andam. */
  const itens = useRef<(HTMLButtonElement | null)[]>([]);
  const jaEntrou = useRef(false);
  const idDoGatilho = useId();
  const ehCampo = variante === 'campo';

  /**
   * Onde o menu cabe na JANELA — não no pai.
   *
   * O menu é desenhado num portal, preso à janela, e não mais dentro da árvore
   * do gatilho. O motivo é concreto: a lateral do painel é um `<Panel>`
   * redimensionável com `overflow-hidden` (Shell.tsx), e um menu `absolute` lá
   * dentro era DECEPADO — no tablet em paisagem os rótulos apareciam sem o
   * começo ("está rodando" no lugar de "onde isso está rodando?"), e não há
   * como escolher o que não dá para ler.
   *
   * `z-index` não resolveria: quem é cortado por `overflow: hidden` de um
   * ancestral não escapa por empilhamento — precisa sair do ancestral. E o
   * `overflow-hidden` de lá não pode cair, porque é ele que segura o conteúdo
   * durante o arrasto da divisória.
   */
  const medir = useCallback((): void => {
    const alvo = gatilho.current;
    if (!alvo) return;
    const r = alvo.getBoundingClientRect();
    const margem = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const largura = Math.min(ehCampo ? r.width : 288, vw - 2 * margem);

    // Alinha pela esquerda do gatilho; se não couber, vira para a esquerda —
    // e no fim é puxado para dentro da janela, que é a garantia de verdade.
    let esquerda = r.left;
    if (esquerda + largura > vw - margem) esquerda = r.right - largura;
    esquerda = Math.max(margem, Math.min(esquerda, vw - largura - margem));

    const abaixo = vh - r.bottom - margem;
    const acimaDe = r.top - margem;
    // Abre para cima quando pediram, ou quando embaixo não cabe e em cima cabe
    // mais: é o caso da pílula no rodapé do compositor.
    const paraCima = acima || (abaixo < 180 && acimaDe > abaixo);
    setCaixa({
      esquerda,
      largura,
      topo: paraCima ? 0 : r.bottom + 4,
      // Ancorado pelo RODAPÉ quando abre para cima: sem saber a altura ainda,
      // é o único jeito de a borda de baixo encostar no gatilho.
      deBaixo: paraCima ? vh - r.top + 4 : 0,
      alturaMax: Math.max(120, Math.min(288, (paraCima ? acimaDe : abaixo) - 4)),
    });
  }, [acima, ehCampo]);

  useEffect(() => {
    if (!aberto) {
      setCaixa(null);
      return;
    }
    medir();
    // Rolar ou redimensionar move o gatilho e deixaria o menu para trás,
    // flutuando sozinho no meio da tela.
    const refazer = (): void => medir();
    window.addEventListener('resize', refazer);
    window.addEventListener('scroll', refazer, true);
    return () => {
      window.removeEventListener('resize', refazer);
      window.removeEventListener('scroll', refazer, true);
    };
  }, [aberto, medir]);

  /*
    Clique-fora fecha — o <details> sozinho não faz isso.

    `pointerdown`, e não `mousedown`: o iOS não sintetiza evento de mouse ao
    tocar em elemento que não é clicável, então tocar em metade da tela não
    fechava este menu — ele grudava. O `Compositor` e a `ListaDeSessoes` já
    tinham corrigido isso nos menus deles, cada um com o motivo escrito ao
    lado; o `Seletor` ficou para trás e herdou o defeito.
  */
  useEffect(() => {
    if (!aberto) return;
    const fecharFora = (e: PointerEvent): void => {
      const alvo = e.target as Node;
      // O menu mora fora de `raiz` desde que virou portal: sem perguntar
      // também a ele, o primeiro clique numa opção contaria como "clique fora",
      // fecharia o menu e a escolha se perderia no caminho.
      if (raiz.current?.contains(alvo) || popover.current?.contains(alvo)) return;
      setAberto(false);
      setPendente(null);
      setExpandida(null);
    };
    document.addEventListener('pointerdown', fecharFora);
    return () => document.removeEventListener('pointerdown', fecharFora);
  }, [aberto]);

  /**
   * Abrir leva o foco para a opção marcada.
   *
   * Só na entrada — daí o `jaEntrou`. As opções são remontadas a cada render
   * (quem chama monta o array com `.map`), e reagir a isso roubaria o foco de
   * volta toda vez que a pessoa expandisse um esforço aninhado.
   */
  useEffect(() => {
    if (!aberto) {
      jaEntrou.current = false;
      return;
    }
    if (jaEntrou.current) return;
    jaEntrou.current = true;
    // Com busca, quem recebe o foco é o campo (`autoFocus`, porque o menu só
    // entra no DOM depois de medido): abrir já é começar a digitar.
    if (busca) return;
    const disponiveis = focaveis();
    (disponiveis.find((b) => b.getAttribute('aria-selected') === 'true') ?? disponiveis[0])?.focus({
      preventScroll: true,
    });
  }, [aberto]);

  // A pergunta de confirmar desmonta a opção clicada; sem isto o foco cairia
  // no corpo da página e quem usa teclado perderia a resposta de vista.
  useEffect(() => {
    if (pendente) botaoConfirmar.current?.focus({ preventScroll: true });
  }, [pendente]);

  function focaveis(): HTMLButtonElement[] {
    return itens.current.filter(
      (b): b is HTMLButtonElement => b !== null && b.isConnected && !b.disabled,
    );
  }

  function fechar(): void {
    setAberto(false);
    setPendente(null);
    setExpandida(null);
    // A próxima abertura começa da lista inteira: reabrir e encontrar a busca
    // de antes, com metade das opções sumidas, parece lista quebrada.
    if (busca && busca.valor !== '') busca.aoMudar('');
  }

  function escolher(opcao: OpcaoDoSeletor<T>, filho?: string): void {
    const texto = opcao.perigoso
      ? (confirmacao?.(opcao) ?? 'Este modo dispensa aprovações. Confirmar?')
      : (confirmacao?.(opcao) ?? null);
    if (texto) {
      setPendente({ id: opcao.id, ...(filho !== undefined ? { filho } : {}), texto });
      return;
    }
    disparar(opcao.id, filho);
  }

  function disparar(id: T, filho?: string, confirmado = false): void {
    fechar();
    aoEscolher(id, filho, confirmado);
  }

  /** Esc fecha e devolve o foco a quem abriu, em qualquer canto do popover. */
  function aoTeclarNaRaiz(evento: React.KeyboardEvent<HTMLDivElement>): void {
    if (evento.key !== 'Escape' || !aberto) return;
    evento.stopPropagation();
    fechar();
    gatilho.current?.focus({ preventScroll: true });
  }

  function andarPelasOpcoes(evento: React.KeyboardEvent<HTMLDivElement>): void {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(evento.key)) return;
    // Dentro do campo de busca, Home e End andam pelo TEXTO. As setas saem do
    // campo para a lista: a de baixo cai na primeira opção, a de cima na última.
    if (evento.target === campoDeBusca.current && (evento.key === 'Home' || evento.key === 'End')) {
      return;
    }
    const disponiveis = focaveis();
    if (disponiveis.length === 0) return;
    evento.preventDefault();
    const ultimo = disponiveis.length - 1;
    const atual = disponiveis.indexOf(document.activeElement as HTMLButtonElement);
    const proximo =
      evento.key === 'Home'
        ? 0
        : evento.key === 'End'
          ? ultimo
          : evento.key === 'ArrowDown'
            ? atual >= ultimo
              ? 0
              : atual + 1
            : atual <= 0
              ? ultimo
              : atual - 1;
    disponiveis[proximo]?.focus({ preventScroll: true });
  }

  /*
    `min-w-0` na pílula: sem ele ela nunca encolhe abaixo do próprio texto.

    O rótulo já tinha `truncate` e teto de 22ch, mas teto não é permissão para
    encolher — a pílula ficava na largura máxima e transbordava o container.
    Medido na coluna estreita de 182 px: com a pasta "Tramitacao Inteligente" a
    pílula dava 172 px e terminava a 2 px da borda; com "Tramitacao
    Previdenciaria Inteligente", 196 px, terminando 22 px do lado de fora.

    Esses 2 px de folga são a razão de o ensaio passar no Windows e falhar no
    Linux do CI: a mesma cadeia de 22 caracteres não mede o mesmo nas duas
    fontes. Nome de pasta é dado do usuário e não tem tamanho máximo — o certo
    é a pílula ceder e reticenciar, não a lista depender da métrica da fonte de
    quem está olhando.

    E são DOIS lugares, não um. `min-w-0` aqui deixa o `<div>` encolher (medido:
    ele foi a 165 px), mas o `<button>` de dentro continuava com 196: no
    Chromium a largura de um `<button>` é *shrink-to-fit*, ele não preenche nem
    cede como um `<div>` — o pai encolhe e o botão transborda o pai. Por isso o
    botão leva `min-w-0 max-w-full`, e é o `max-w-full` que o prende à caixa
    que acabou de ceder. Sem ele, o `truncate` do rótulo nunca tem por que
    disparar: não falta espaço DENTRO do botão, sobra botão fora do pai.
  */
  return (
    <div
      ref={raiz}
      onKeyDown={aoTeclarNaRaiz}
      className={'relative ' + (ehCampo ? 'w-full' : 'min-w-0')}
    >
      <button
        ref={gatilho}
        type="button"
        id={idDoGatilho}
        onClick={() => (aberto ? fechar() : setAberto(true))}
        onKeyDown={(e) => {
          // A seta abre, como no `<select>` que saiu daqui.
          if (!aberto && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setAberto(true);
          }
        }}
        disabled={ocupado || Boolean(bloqueado)}
        title={bloqueado ?? titulo}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        {...(idDoRotulo ? { 'aria-labelledby': `${idDoRotulo} ${idDoGatilho}` } : {})}
        className={
          'flex items-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/70 disabled:opacity-50 ' +
          (ehCampo
            ? 'w-full justify-between gap-2 rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-left text-sm text-zinc-100 hover:border-zinc-500'
            : 'min-w-0 max-w-full gap-1 rounded-md border border-zinc-800 px-2 py-1 text-xs text-zinc-400 hover:border-zinc-600 hover:text-zinc-200 pointer-coarse:min-h-11 pointer-coarse:px-3 pointer-coarse:text-sm')
        }
      >
        {icone ? <span className="flex shrink-0 items-center">{icone}</span> : null}
        <span className={ehCampo ? 'min-w-0 flex-1 truncate' : 'max-w-[22ch] truncate'}>
          {rotulo}
        </span>
        <IconeAbrir
          tamanho={ehCampo ? 16 : 14}
          className={'text-zinc-500 transition ' + (aberto ? 'rotate-180' : '')}
        />
      </button>

      {aberto && caixa
        ? createPortal(
            <div
              ref={popover}
              role="listbox"
              aria-label={titulo}
              /*
                A marca que diz "isto ainda é o controle, não é 'fora'".
                Quem escuta toque no documento para fechar o que hospeda o
                seletor tem de perguntar por ela antes de fechar.
              */
              data-popover=""
              onKeyDown={andarPelasOpcoes}
              style={{
                left: caixa.esquerda,
                width: caixa.largura,
                maxHeight: caixa.alturaMax,
                ...(caixa.deBaixo ? { bottom: caixa.deBaixo } : { top: caixa.topo }),
              }}
              className="fixed z-50 overflow-y-auto rounded-md border border-zinc-700 bg-zinc-900 p-1.5 text-xs shadow-lg shadow-black/40"
            >
          {pendente ? (
            <div className="space-y-2 p-1.5">
              <p className="text-amber-200">{pendente.texto}</p>
              <div className="flex gap-2">
                <button
                  ref={botaoConfirmar}
                  type="button"
                  onClick={() => disparar(pendente.id, pendente.filho, true)}
                  className="flex-1 rounded bg-amber-600 px-2 py-1.5 font-medium text-white hover:bg-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                >
                  Confirmar
                </button>
                <button
                  type="button"
                  onClick={() => setPendente(null)}
                  className="flex-1 rounded border border-zinc-700 px-2 py-1.5 text-zinc-300 hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/70"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <>
            {busca ? (
              /*
                Grudado no topo do menu: rolar a lista não pode levar o campo
                embora, senão refinar a busca exige rolar de volta.
              */
              <div className="sticky -top-1.5 z-10 -mx-1.5 -mt-1.5 mb-1 border-b border-zinc-800 bg-zinc-900 p-1.5">
                <label className="flex items-center gap-1.5 rounded border border-zinc-700 bg-zinc-950 px-2 focus-within:border-zinc-500">
                  <IconeBuscar tamanho={14} className="text-zinc-500" />
                  <input
                    ref={campoDeBusca}
                    type="text"
                    // O menu só entra no DOM depois de medido; `autoFocus` é o
                    // que pega o campo no instante em que ele monta.
                    autoFocus
                    value={busca.valor}
                    onChange={(e) => busca.aoMudar(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter escolhe a primeira: buscar e confirmar sem tirar a
                      // mão do teclado é o caminho de quem já sabe o nome.
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      const primeira = opcoes.find(
                        (o) => !o.desabilitada && !(o.filhos && o.filhos.length > 0),
                      );
                      if (primeira) escolher(primeira);
                    }}
                    placeholder={busca.dica ?? 'buscar…'}
                    aria-label={'Buscar em ' + titulo}
                    autoComplete="off"
                    spellCheck={false}
                    /*
                      16 px no dedo, e não 14: abaixo disso o Safari do iPhone dá
                      zoom na página inteira ao focar o campo, e o menu fixo
                      fica fora do lugar.
                    */
                    className="min-w-0 flex-1 bg-transparent py-1.5 text-xs text-zinc-100 outline-none placeholder:text-zinc-600 pointer-coarse:py-2.5 pointer-coarse:text-base"
                  />
                </label>
              </div>
            ) : null}
            {busca && opcoes.length === 0 ? (
              <p role="status" className="px-1.5 py-2 text-zinc-500">
                {busca.carregando ? 'buscando…' : 'nada encontrado'}
              </p>
            ) : null}
            {opcoes.map((opcao, i) => (
              <div key={opcao.id}>
                <button
                  ref={(no) => {
                    itens.current[i] = no;
                  }}
                  type="button"
                  role="option"
                  aria-selected={opcao.id === valor}
                  disabled={opcao.desabilitada}
                  title={opcao.desabilitada ? opcao.motivo : undefined}
                  onClick={() =>
                    opcao.filhos && opcao.filhos.length > 0
                      ? setExpandida((atual) => (atual === opcao.id ? null : opcao.id))
                      : escolher(opcao)
                  }
                  className={
                    // 24 px de altura era o que havia aqui: o gatilho seguia o
                    // padrão da casa (44 px) e o menu que ele abre, não. Quem
                    // erra o alvo conclui que o controle não funciona — e foi
                    // exatamente esse o relato.
                    'flex w-full items-start gap-2 rounded px-1.5 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/70 disabled:cursor-not-allowed disabled:opacity-40 ' +
                    'active:bg-zinc-800 pointer-coarse:min-h-11 pointer-coarse:items-center pointer-coarse:px-2 pointer-coarse:text-sm ' +
                    (opcao.id === valor ? 'bg-zinc-800 ' : 'hover:bg-zinc-800 ') +
                    (opcao.perigoso ? 'text-amber-300' : 'text-zinc-200')
                  }
                >
                  <span className="mt-0.5 flex w-4 shrink-0 justify-center">
                    {opcao.id === valor ? (
                      <IconeConfirmado tamanho={14} className="text-emerald-400" />
                    ) : null}
                  </span>
                  {opcao.icone ? (
                    <span className="mt-0.5 flex shrink-0 items-center">{opcao.icone}</span>
                  ) : null}
                  <span className="min-w-0 flex-1">
                    {opcao.rotulo}
                    {opcao.descricao ? (
                      /*
                        Opção FECHADA quebra linha em vez de reticenciar: ali a
                        linha de baixo é o motivo ("falta a chave do NanoGPT,
                        um ADMIN cadastra em Configurações"), e o fim cortado
                        era justamente o gesto que resolve. No telefone o
                        `title` não aparece — o texto é tudo que a pessoa tem.
                      */
                      <span
                        className={
                          'block text-[11px] text-zinc-500 ' +
                          (opcao.desabilitada ? 'whitespace-normal break-words' : 'truncate')
                        }
                      >
                        {opcao.descricao}
                      </span>
                    ) : null}
                  </span>
                  {opcao.filhos && opcao.filhos.length > 0 ? (
                    expandida === opcao.id ? (
                      <IconeAbrir tamanho={14} className="mt-0.5 text-zinc-600" />
                    ) : (
                      <IconeFechado tamanho={14} className="mt-0.5 text-zinc-600" />
                    )
                  ) : null}
                </button>
                {expandida === opcao.id && opcao.filhos ? (
                  <div className="mb-1 ml-6 flex flex-wrap gap-1">
                    {opcao.filhos.map((filho) => (
                      <button
                        key={filho.id}
                        type="button"
                        onClick={() => escolher(opcao, filho.id)}
                        // As cinco pílulas de esforço eram ~32×20 px com 4 px
                        // entre elas: acertar `high` em vez de `xhigh` com o
                        // polegar era loteria, e loteria perdida parece
                        // controle quebrado.
                        className="rounded border border-zinc-700 px-1.5 py-0.5 font-mono text-[11px] text-zinc-300 hover:border-zinc-500 hover:text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/70 active:border-zinc-400 pointer-coarse:min-h-11 pointer-coarse:min-w-11 pointer-coarse:px-3 pointer-coarse:text-sm"
                      >
                        {filho.rotulo}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            </>
          )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
